import { createBrowserHistory, createMemoryHistory, History } from 'history';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Router, Switch, useLocation } from 'react-router-dom';

import { NotificationMessage } from '@/components';
import { ProfileIdentificationStatusAPIModel } from '@/generated/api/fps/api';
import {
  withApplicationDetection,
  withAuthorizationGuard,
  withErrorBoundary,
  withFeatureToggle,
  withFrameDetection,
  withGlobalBackdrop,
  withGlobalInitialization,
  withI18n,
  withI18nSupport,
  withMaintenanceCheck,
  withMobileDetection,
  withStore,
  withTheme,
} from '@/hocs';
import { withSentryInitialization } from '@/infrastructure/sentry';
import { BlankLayout, StatusLayout } from '@/layout';
import { makeSelectApplicationType, makeSelectIsFrameView } from '@/model/global/selectors';
import { ApplicationType, UIState } from '@/model/global/types';
import { makeSelectIdentificationState, makeSelectProfileIdentificationStatus } from '@/model/identification/selectors';
import { IdentificationState } from '@/model/identification/types';
import { DemoPages, IdentificationPages, WalletsPages, WithdrawalPages } from '@/pages';

const selectIdentificationState = makeSelectIdentificationState();
const selectProfileIdentificationStatus = makeSelectProfileIdentificationStatus();

const ValidationContext = React.createContext({ failed: false });

const withIdentificationValidation = (Component: React.FC) => (props: { [k in string]: unknown }): JSX.Element => {
  // const { pathname } = useLocation();
  const state = useSelector(selectIdentificationState);
  const isPhasePassed = state === IdentificationState.PASSED;
  // const validationContext = useContext(ValidationContext);
  // const isRouteValid = isPhasePassed || pathname.startsWith(`/${UIState.IDENTIFICATION}`);
  // return (
  //   <ValidationContext.Provider value={{ failed: validationContext.failed || !isPhasePassed }}>
  //     {validationContext.failed && !isRouteValid ? (
  //       <Redirect to={`/${UIState.IDENTIFICATION}`} />
  //     ) : (
  //       <Component {...props} />
  //     )}
  //   </ValidationContext.Provider>
  // );
  return isPhasePassed ? (
    <Component {...props} />
  ) : (
    <StatusLayout status="failed" height={220}>
      <NotificationMessage severity="error" title={<FormattedMessage id="verification.not-verified.title" />}>
        <FormattedMessage id="verification.not-verified.content" />
      </NotificationMessage>
    </StatusLayout>
  );
};

const withVerificationValidation = (Component: React.FC) => (props: { [k in string]: unknown }): JSX.Element => {
  const status = useSelector(selectProfileIdentificationStatus);
  const state = useSelector(selectIdentificationState);
  const isPreviousPhasePassed = state === IdentificationState.PASSED;
  const isPhasePassed = status === ProfileIdentificationStatusAPIModel.Verified;
  return !isPreviousPhasePassed || isPhasePassed ? (
    <Component {...props} />
  ) : (
    <StatusLayout status="failed" height={220}>
      <NotificationMessage severity="error" title={<FormattedMessage id="verification.not-verified.title" />}>
        <FormattedMessage id="verification.not-verified.content" />
      </NotificationMessage>
    </StatusLayout>
  );
};

const withDestinationValidation = (destinationState: UIState) => (Component: React.FC) => (
  props: { [k in string]: unknown },
): JSX.Element => {
  const { pathname } = useLocation();
  const validationContext = useContext(ValidationContext);
  const isRouteValid = pathname.startsWith(`/${destinationState}`);
  return validationContext.failed || isRouteValid ? <Component {...props} /> : <Redirect to={`/${destinationState}`} />;
};

const withRouteValidation = (destinationState: UIState) => (Component: React.FC): React.FC =>
  withIdentificationValidation(withVerificationValidation(withDestinationValidation(destinationState)(Component)));

const DefaultRoute: React.FC<RouteComponentProps> = ({ location: { pathname } }) => {
  console.warn(`URL not found: ${pathname}`);
  return <Redirect to={`/${UIState.WITHDRAWAL}`} />;
};

const WithdrawalRoutes: React.FC = () => (
  <Switch>
    <Route
      path={`/${UIState.IDENTIFICATION}`}
      component={withRouteValidation(UIState.WITHDRAWAL)(IdentificationPages)}
    />
    <Route path={`/${UIState.WALLETS}`} component={withRouteValidation(UIState.WALLETS)(WalletsPages)} />
    <Route path={`/${UIState.WITHDRAWAL}`} component={withRouteValidation(UIState.WITHDRAWAL)(WithdrawalPages)} />
    <Redirect from="/" to={`/${UIState.WITHDRAWAL}`} />
    <Route component={DefaultRoute} />
  </Switch>
);

// const InvoiceRoutes: React.FC = () => (
//   <Switch>
//     <Route path={`/${UIState.IDENTIFICATION}`} component={withRouteValidation(UIState.INVOICE)(IdentificationPages)} />
//     <Route path={`/${UIState.INVOICE}`} component={withRouteValidation(UIState.INVOICE)(InvoicePages)} />
//     <Redirect from="/" to={`/${UIState.IDENTIFICATION}`} />
//     <Route component={DefaultRoute} />
//   </Switch>
// );

const withWalletsValidation = (Component: React.FC): React.FC => withDestinationValidation(UIState.WALLETS)(Component);

const WalletRoutes: React.FC = () => (
  <Switch>
    <Route path={`/${UIState.WALLETS}`} component={withWalletsValidation(WalletsPages)} />
    <Redirect from="/" to={`/${UIState.WALLETS}`} />
    <Route component={DefaultRoute} />
  </Switch>
);

const withDemoRouteValidation = (Component: React.FC): ((props: { [k in string]: unknown }) => JSX.Element) =>
  withIdentificationValidation(withDestinationValidation(UIState.WITHDRAWAL)(Component));

const DemoRoutes: React.FC = () => (
  <Switch>
    <Route path={`/${UIState.IDENTIFICATION}`} component={withDemoRouteValidation(IdentificationPages)} />
    <Route path={`/${UIState.WITHDRAWAL}`} component={withDemoRouteValidation(DemoPages)} />
    <Redirect from="/" to={`/${UIState.IDENTIFICATION}`} />
    <Route component={DefaultRoute} />
  </Switch>
);

interface AppRouterProps {
  applicationType: ApplicationType;
}

const selectIsFrame = makeSelectIsFrameView();
const AppRouter: React.FC<AppRouterProps> = ({ applicationType }: AppRouterProps) => {
  const [history, setHistory] = useState<History | undefined>();
  const isFrame = useSelector(selectIsFrame);
  useEffect(() => {
    setHistory(
      isFrame
        ? createMemoryHistory()
        : createBrowserHistory({ basename: `${window.env.PUBLIC_URL}${applicationType}` }),
    );
  }, [applicationType, isFrame]);
  if (!history) {
    return <BlankLayout />;
  }
  return (
    <Router history={history}>
      {applicationType === ApplicationType.DEMO && <DemoRoutes />}
      {applicationType === ApplicationType.WITHDRAWAL && <WithdrawalRoutes />}
      {/* {applicationType === ApplicationType.INVOICE && <InvoiceRoutes />} */}
      {applicationType === ApplicationType.WALLET && <WalletRoutes />}
    </Router>
  );
};

const selectType = makeSelectApplicationType();

const App: React.FC = () => {
  const applicationType = useSelector(selectType);
  const isFrame = useSelector(selectIsFrame);
  const [history, setHistory] = useState<History | undefined>(undefined);
  useEffect(() => {
    if (!history) {
      setHistory(isFrame ? createMemoryHistory() : createBrowserHistory());
    }
  }, [history, isFrame]);

  return history ? (
    <Router history={history}>
      <Switch>
        <Route path={`/${applicationType}`}>
          <AppRouter applicationType={applicationType} />
        </Route>
        <Redirect to={`/${applicationType}`} />
      </Switch>
    </Router>
  ) : (
    <BlankLayout />
  );
};

// prettier-ignore
export default withSentryInitialization(
  withStore(
    withFrameDetection(
      withI18nSupport(
        withI18n(
          withTheme(
            withMobileDetection(
              withErrorBoundary(
                withFeatureToggle(
                  withMaintenanceCheck('allDisabled')(
                    withGlobalBackdrop(
                      withGlobalInitialization(
                        withApplicationDetection(
                          withAuthorizationGuard(
                            App,
                          ),
                        ),
                      ),
                    ),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
);
